// src/pages/WorkflowAiPage.jsx

/**
 * Workflow AI Page – allows a user to type a natural‑language description of a workflow
 * and see the generated DSL steps.
 *
 * The page consists of:
 *   • A textarea for the description
 *   • A "Generate" button (disabled while loading or when input is empty)
 *   • A loading spinner while the backend is processing
 *   • A formatted list of generated steps (or an error message)
 *
 * The backend endpoint is expected at POST /api/workflows/ai and to return JSON:
 *   { steps: [ { type: string, config: object } ] }
 * If the request fails we surface the error message.
 */

import { useState } from "react";

export default function WorkflowAiPage() {
  const [description, setDescription] = useState("");
  const [steps, setSteps] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleGenerate = async () => {
    if (!description.trim()) return;
    setLoading(true);
    setError(null);
    setSteps(null);
    try {
      const res = await fetch("/api/workflows/ai", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ description: description.trim() }),
      });
      if (!res.ok) {
        const err = await res.text();
        throw new Error(err || `Server responded with ${res.status}`);
      }
      const data = await res.json();
      setSteps(data.steps || []);
    } catch (e) {
      console.error(e);
      setError(e.message || "Unexpected error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-4">AI Workflow Generator</h1>
      <p className="mb-4 text-gray-600">
        Describe the workflow you want in plain English and let the AI create the DSL steps for you.
      </p>
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="e.g., When a new lead is added, send a welcome email, then wait 2 days and send a follow‑up SMS."
        className="w-full h-40 p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none mb-4"
        disabled={loading}
      />
      <div className="flex items-center space-x-4 mb-6">
        <button
          onClick={handleGenerate}
          disabled={loading || !description.trim()}
          className={`px-4 py-2 rounded-md font-medium text-white ${loading || !description.trim() ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}
        >
          {loading ? "Generating…" : "Generate Workflow"}
        </button>
        {loading && (
          <div className="w-5 h-5 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin" />
        )}
      </div>

      {error && (
        <div className="p-4 mb-4 bg-red-100 border border-red-200 text-red-800 rounded-md">
          <strong>Error:</strong> {error}
        </div>
      )}

      {steps && steps.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Generated Steps</h2>
          <ol className="list-decimal list-inside space-y-2">
            {steps.map((step, idx) => (
              <li key={idx} className="bg-gray-50 p-3 rounded-md border border-gray-200">
                <div className="font-medium text-gray-800">{step.type}</div>
                {step.config && Object.keys(step.config).length > 0 && (
                  <pre className="mt-1 text-sm text-gray-600 whitespace-pre-wrap">
                    {JSON.stringify(step.config, null, 2)}
                  </pre>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}

      {steps && steps.length === 0 && !loading && !error && (
        <div className="text-gray-500">No steps generated. Try refining your description.</div>
      )}
    </div>
  );
}
